import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, FileUp, Upload, LoaderCircle, Plus } from "lucide-react";
import { Lead, LeadStatus, LeadPriority } from "./lead-card";
import { v4 as uuidv4 } from "uuid";
import { Alert, AlertDescription } from "./ui/alert";
import { Progress } from "./ui/progress";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Folder } from "@/services/foldersService";

interface CSVImportProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (leads: Partial<Lead>[], folderId?: string) => void;
  userFolders?: Folder[];
  marketplaceFields?: {
    price: string;
    folderName: string;
    description: string;
    onPriceChange: (value: string) => void;
    onFolderChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    folders: string[];
  };
}

interface ColumnMapping {
  field: string;
  header: string;
}

const DEFAULT_MAPPINGS: ColumnMapping[] = [
  { field: "firstName", header: "Vorname" },
  { field: "lastName", header: "Nachname" },
  { field: "position", header: "Beruf" },
  { field: "attachment", header: "Aufhänger" },
  { field: "email", header: "E-Mail" },
  { field: "phone", header: "Telefon" },
  { field: "status", header: "Status" },
  { field: "priority", header: "Priorität" },
  { field: "notes", header: "Notizen" },
  { field: "assignedTo", header: "Zugewiesen an" },
  { field: "nextContactDate", header: "Nächster Kontakt" },
  { field: "lastContactDate", header: "Letzter Kontakt" },
];

const translateHeaderToGerman = (header: string): string => {
  const headerLower = header.toLowerCase().trim();

  // Mapping von englischen zu deutschen Spaltennamen
  const englishToGerman: Record<string, string> = {
    'first name': 'Vorname',
    'firstname': 'Vorname',
    'first_name': 'Vorname',
    'first-name': 'Vorname',
    'last name': 'Nachname',
    'lastname': 'Nachname',
    'last_name': 'Nachname',
    'last-name': 'Nachname',
    'email': 'E-Mail',
    'e-mail': 'E-Mail',
    'email address': 'E-Mail',
    'position': 'Beruf',
    'job': 'Beruf',
    'job title': 'Beruf',
    'title': 'Beruf',
    'phone': 'Telefon',
    'telephone': 'Telefon',
    'phone number': 'Telefon',
    'status': 'Status',
    'priority': 'Priorität',
    'notes': 'Notizen',
    'note': 'Notizen',
    'notiz': 'Notizen',
    'comment': 'Notizen',
    'description': 'Notizen',
    'assigned to': 'Zugewiesen an',
    'assigned_to': 'Zugewiesen an',
    'next contact': 'Nächster Kontakt',
    'next_contact': 'Nächster Kontakt',
    'next contact date': 'Nächster Kontakt',
    'last contact': 'Letzter Kontakt',
    'last_contact': 'Letzter Kontakt',
    'last contact date': 'Letzter Kontakt',
    'attachment': 'Aufhänger',
    'aufhaenger': 'Aufhänger',
    'aufhänger': 'Aufhänger',
    'company': 'Firma',
    'firma': 'Firma',
    'unternehmen': 'Firma',
    'address': 'Adresse',
    'adresse': 'Adresse',
    'street': 'Straße',
    'strasse': 'Straße',
    'straße': 'Straße',
    'city': 'Stadt',
    'stadt': 'Stadt',
    'zip': 'PLZ',
    'postal code': 'PLZ',
    'postal_code': 'PLZ',
    'country': 'Land',
    'land': 'Land',
    'website': 'Webseite',
    'webseite': 'Webseite',
    'url': 'Webseite',
    'source': 'Quelle',
    'quelle': 'Quelle',
    'created': 'Erstellt am',
    'created_at': 'Erstellt am',
    'created at': 'Erstellt am',
    'updated': 'Aktualisiert am',
    'updated_at': 'Aktualisiert am',
    'updated at': 'Aktualisiert am'
  };

  return englishToGerman[headerLower] || header;
};

export function CSVImport({ isOpen, onClose, onImport, userFolders, marketplaceFields }: CSVImportProps) {
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [step, setStep] = useState<"upload" | "mapping" | "preview" | "importing">("upload");
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [importingStatus, setImportingStatus] = useState<string>("Verarbeite CSV-Daten...");
  const [newFolderName, setNewFolderName] = useState<string>("");
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");

  const resetState = () => {
    setFile(null);
    setHeaders([]);
    setPreviewData([]);
    setMappings({});
    setStep("upload");
    setError(null);
    setTotalRows(0);
    setSelectedFolderId("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const parseCSV = (text: string, previewOnly: boolean = false) => {
    // First, try to detect the delimiter
    const firstLine = text.split(/\r\n|\n|\r/)[0];
    let delimiter = ',';

    // Check for common delimiters
    const delimiters = [',', ';', '\t'];
    const delimiterCounts = delimiters.map(d => ({
      delimiter: d,
      count: (firstLine.match(new RegExp(d, 'g')) || []).length
    }));

    // Use the delimiter that appears most often
    const mostFrequent = delimiterCounts.reduce((prev, current) =>
      (current.count > prev.count) ? current : prev
    );

    if (mostFrequent.count > 0) {
      delimiter = mostFrequent.delimiter;
    }

    // Now parse the CSV with the detected delimiter
    const result: string[][] = [];
    const lines = text.split(/\r\n|\n|\r/);

    lines.forEach(line => {
      // Skip empty lines
      if (line.trim() === '') return;

      const row: string[] = [];
      let insideQuotes = false;
      let currentValue = '';

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          // Handle escaped quotes (double quotes)
          if (i + 1 < line.length && line[i + 1] === '"') {
            currentValue += '"';
            i++; // Skip the next quote
          } else {
            insideQuotes = !insideQuotes;
          }
        } else if (char === delimiter && !insideQuotes) {
          row.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }

      // Push the last value
      row.push(currentValue.trim());
      result.push(row);
    });

    // Try to detect and map headers
    if (result.length > 0) {
      const headers = result[0];
      const headerMappings: Record<string, string> = {
        // English variations
        'first name': 'firstName',
        'firstname': 'firstName',
        'first_name': 'firstName',
        'first-name': 'firstName',
        'last name': 'lastName',
        'lastname': 'lastName',
        'last_name': 'lastName',
        'last-name': 'lastName',
        'email': 'email',
        'e-mail': 'email',
        'email address': 'email',
        'position': 'position',
        'job': 'position',
        'job title': 'position',
        'title': 'position',
        'phone': 'phone',
        'telephone': 'phone',
        'phone number': 'phone',
        'status': 'status',
        'priority': 'priority',
        'notes': 'notes',
        'note': 'notes',
        'comment': 'notes',
        'description': 'notes',
        'assigned to': 'assignedTo',
        'assigned_to': 'assignedTo',
        'next contact': 'nextContactDate',
        'next_contact': 'nextContactDate',
        'next contact date': 'nextContactDate',
        'last contact': 'lastContactDate',
        'last_contact': 'lastContactDate',
        'last contact date': 'lastContactDate',
        'attachment': 'attachment',
        'aufhaenger': 'attachment',
        'aufhänger': 'attachment',
        // German variations
        'vorname': 'firstName',
        'nachname': 'lastName',
        // 'e-mail': 'email', - duplicate
        'emailadresse': 'email',
        'beruf': 'position',
        // 'position': 'position', - duplicate
        'telefon': 'phone',
        'telefonnummer': 'phone',
        // 'status': 'status', - duplicate
        'priorität': 'priority',
        'prioritaet': 'priority',
        'notizen': 'notes',
        'notiz': 'notes',
        'bemerkungen': 'notes',
        'zugewiesen an': 'assignedTo',
        'zugewiesen_an': 'assignedTo',
        'nächster kontakt': 'nextContactDate',
        'naechster_kontakt': 'nextContactDate',
        'letzter kontakt': 'lastContactDate',
        'letzter_kontakt': 'lastContactDate',
        // 'aufhänger': 'attachment', - duplicate
        // 'aufhaenger': 'attachment' - duplicate
      };

      // Map headers to standard field names
      const mappedHeaders = headers.map(header => {
        const normalizedHeader = header.toLowerCase().trim();
        return headerMappings[normalizedHeader] || header;
      });

      result[0] = mappedHeaders;
    }

    // If preview only, return just the headers and first 5 rows for preview
    if (previewOnly && result.length > 0) {
      return {
        headers: result[0],
        previewRows: result.slice(1, 6),
        totalRows: result.length - 1 // Total rows excluding header
      };
    }

    // Otherwise return all data
    return {
      headers: result[0],
      rows: result.slice(1),
      totalRows: result.length - 1
    };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const buffer = event.target?.result as ArrayBuffer;

        // First try UTF-8 with BOM detection
        const firstBytes = new Uint8Array(buffer.slice(0, 4));
        let text = '';
        let usedEncoding = '';

        // Check for UTF-8 BOM (EF BB BF)
        if (firstBytes[0] === 0xEF && firstBytes[1] === 0xBB && firstBytes[2] === 0xBF) {
          const decoder = new TextDecoder('utf-8');
          text = decoder.decode(buffer.slice(3)); // Skip BOM
          usedEncoding = 'utf-8 (with BOM)';
          console.log('Detected UTF-8 with BOM');
        } else {
          // Try UTF-8 first without BOM
          try {
            const decoder = new TextDecoder('utf-8');
            text = decoder.decode(buffer);
            usedEncoding = 'utf-8';

            // Verify if the text contains valid German characters
            const germanChars = ['ä', 'ö', 'ü', 'Ä', 'Ö', 'Ü', 'ß'];
            const hasValidGermanChars = germanChars.some(char => text.includes(char));
            const hasInvalidChars = text.includes('') || text.includes('') || text.includes('');

            if (!hasValidGermanChars || hasInvalidChars) {
              // If UTF-8 didn't work well, try Windows-1252 (common for German Windows systems)
              const decoder = new TextDecoder('windows-1252');
              text = decoder.decode(buffer);
              usedEncoding = 'windows-1252';
              console.log('Using Windows-1252 encoding');
            }
          } catch (e) {
            // If UTF-8 fails, fall back to Windows-1252
            const decoder = new TextDecoder('windows-1252');
            text = decoder.decode(buffer);
            usedEncoding = 'windows-1252';
            console.log('Fallback to Windows-1252 encoding');
          }
        }

        // Log the final encoding used
        console.log('Final encoding used:', usedEncoding);

        const { headers, previewRows, totalRows } = parseCSV(text, true);

        if (!headers || headers.length === 0) {
          setError("Die Datei scheint leer zu sein oder hat ein ungültiges Format");
          return;
        }

        setHeaders(headers);
        setPreviewData(previewRows);
        setTotalRows(totalRows);

        // Auto-map columns by name
        const initialMappings: Record<string, string> = {};
        headers.forEach((header, index) => {
          const matchMapping = DEFAULT_MAPPINGS.find(m =>
            header.toLowerCase().includes(m.field.toLowerCase()) ||
            m.header.toLowerCase().includes(header.toLowerCase())
          );
          if (matchMapping) {
            initialMappings[index.toString()] = matchMapping.field;
          } else {
            initialMappings[index.toString()] = "not_mapped";
          }
        });

        setMappings(initialMappings);
        setStep("mapping");
        setError(null);
      } catch (err) {
        console.error("Error parsing CSV:", err);
        setError("Fehler beim Lesen der Datei. Bitte überprüfen Sie das Format.");
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleMappingChange = (columnIndex: string, value: string) => {
    setMappings(prev => ({
      ...prev,
      [columnIndex]: value,
    }));
  };

  const handlePreview = () => {
    const validMappings = Object.values(mappings).filter(field => field !== "not_mapped");
    if (validMappings.length === 0) {
      setError("Bitte mindestens eine Spalte zuordnen");
      return;
    }
    setStep("preview");
  };

  const handleImport = async () => {
    if (!file) return;

    setStep("importing");
    setImportProgress(0);
    setImportingStatus("Verarbeite CSV-Daten...");

    await new Promise(resolve => setTimeout(resolve, 300));

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        setImportingStatus("Analysiere Daten...");
        setImportProgress(10);
        await new Promise(resolve => setTimeout(resolve, 500));

        const buffer = event.target?.result as ArrayBuffer;
        let text = '';
        let usedEncoding = '';

        // Check for UTF-8 BOM (EF BB BF)
        const firstBytes = new Uint8Array(buffer.slice(0, 4));
        if (firstBytes[0] === 0xEF && firstBytes[1] === 0xBB && firstBytes[2] === 0xBF) {
          const decoder = new TextDecoder('utf-8');
          text = decoder.decode(buffer.slice(3)); // Skip BOM
          usedEncoding = 'utf-8 (with BOM)';
          console.log('Detected UTF-8 with BOM');
        } else {
          // Try UTF-8 first without BOM
          try {
            const decoder = new TextDecoder('utf-8');
            text = decoder.decode(buffer);
            usedEncoding = 'utf-8';

            // Verify if the text contains valid German characters
            const germanChars = ['ä', 'ö', 'ü', 'Ä', 'Ö', 'Ü', 'ß'];
            const hasValidGermanChars = germanChars.some(char => text.includes(char));
            const hasInvalidChars = text.includes('') || text.includes('') || text.includes('');

            if (!hasValidGermanChars || hasInvalidChars) {
              // If UTF-8 didn't work well, try Windows-1252 (common for German Windows systems)
              const decoder = new TextDecoder('windows-1252');
              text = decoder.decode(buffer);
              usedEncoding = 'windows-1252';
              console.log('Using Windows-1252 encoding');
            }
          } catch (e) {
            // If UTF-8 fails, fall back to Windows-1252
            const decoder = new TextDecoder('windows-1252');
            text = decoder.decode(buffer);
            usedEncoding = 'windows-1252';
            console.log('Fallback to Windows-1252 encoding');
          }
        }

        // Log the final encoding used
        console.log('Final encoding used:', usedEncoding);

        const { headers, rows } = parseCSV(text, false);

        setImportingStatus("Extrahiere Informationen...");
        setImportProgress(20);
        await new Promise(resolve => setTimeout(resolve, 700));

        // Transform CSV data to Lead objects
        const leads: Partial<Lead>[] = [];
        const totalRows = rows.length;

        // Calculate how many rows to process per batch based on total rows
        const updateFrequency = Math.max(Math.floor(totalRows / 20), 1);

        for (let i = 0; i < totalRows; i++) {
          const row = rows[i];
          const lead: Partial<Lead> = { id: uuidv4() };

          Object.entries(mappings).forEach(([columnIndex, fieldName]) => {
            if (fieldName === "not_mapped") return;

            const index = parseInt(columnIndex);
            const value = row[index];

            if (value) {
              if (fieldName === "nextContactDate" || fieldName === "lastContactDate") {
                // Try to parse as date
                try {
                  // Try to handle different date formats: YYYY-MM-DD, DD.MM.YYYY, etc.
                  let dateValue: Date | null = null;

                  // Check if it's ISO format
                  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
                    dateValue = new Date(value);
                  }
                  // Check if it's German format (DD.MM.YYYY)
                  else if (/^\d{1,2}\.\d{1,2}\.\d{4}/.test(value)) {
                    const [day, month, year] = value.split('.').map(Number);
                    dateValue = new Date(year, month - 1, day);
                  }
                  // Try as regular date
                  else {
                    dateValue = new Date(value);
                  }

                  if (dateValue && !isNaN(dateValue.getTime())) {
                    (lead as any)[fieldName] = dateValue;
                  } else {
                    // Initialize with empty string if not a valid date
                    (lead as any)[fieldName] = "";
                  }
                } catch (err) {
                  console.error("Failed to parse date", err);
                  // Initialize with empty string if parsing fails
                  (lead as any)[fieldName] = "";
                }
              } else if (fieldName === "status") {
                // Validate status
                const statusValue = value.toLowerCase();
                const validStatus = ["new", "in-progress", "callback", "offer", "negotiation", "won", "lost", "paused"];

                // Try to match with German translations
                const germanToEnglish: Record<string, LeadStatus> = {
                  "neu": "new",
                  "in bearbeitung": "in-progress",
                  "rückruf": "callback",
                  "angebot": "offer",
                  "verhandlung": "negotiation",
                  "gewonnen": "won",
                  "verloren": "lost",
                  "pausiert": "paused"
                };

                if (validStatus.includes(statusValue)) {
                  lead.status = statusValue as LeadStatus;
                } else {
                  // Try to match with German terms
                  for (const [german, english] of Object.entries(germanToEnglish)) {
                    if (statusValue.includes(german)) {
                      lead.status = english;
                      break;
                    }
                  }

                  // Default to new if no match
                  if (!lead.status) {
                    lead.status = "new";
                  }
                }
              } else if (fieldName === "priority") {
                // Validate priority
                const priorityValue = value.toLowerCase();
                const validPriority = ["low", "medium", "high"];

                // Try to match with German translations
                const germanToEnglish: Record<string, LeadPriority> = {
                  "niedrig": "low",
                  "mittel": "medium",
                  "hoch": "high"
                };

                if (validPriority.includes(priorityValue)) {
                  lead.priority = priorityValue as LeadPriority;
                } else {
                  // Try to match with German terms
                  for (const [german, english] of Object.entries(germanToEnglish)) {
                    if (priorityValue.includes(german)) {
                      lead.priority = english;
                      break;
                    }
                  }

                  // Default to medium if no match
                  if (!lead.priority) {
                    lead.priority = "medium";
                  }
                }
              } else {
                (lead as any)[fieldName] = value;
              }
            }
          });

          // Initialize date fields that weren't mapped or were empty
          if (!lead.nextContactDate) {
            lead.nextContactDate = "";
          }

          if (!lead.lastContactDate) {
            lead.lastContactDate = "";
          }

          // Only add valid leads - now checking for firstName and lastName only
          if (lead.firstName && lead.lastName) {
            leads.push(lead);
          }

          // Update progress
          if (i % updateFrequency === 0 || i === totalRows - 1) {
            const progress = 20 + Math.round((i + 1) / totalRows * 60);
            setImportProgress(progress);
            setImportingStatus(`Verarbeite Datensätze: ${i + 1} von ${totalRows}`);
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }

        if (leads.length === 0) {
          setError("Keine gültigen Leads gefunden. Leads müssen mindestens Vorname und Nachname haben.");
          setStep("preview");
          return;
        }

        setImportingStatus(`Speichere ${leads.length} Leads...`);
        setImportProgress(85);

        // Artificially slow down the final part to show progress
        await new Promise(resolve => setTimeout(resolve, 1000));
        setImportProgress(95);

        // Add another delay before completing to ensure user sees the final status
        await new Promise(resolve => setTimeout(resolve, 500));

        // Finish at 100%
        setImportProgress(100);

        // Small final delay before completion for better UX
        await new Promise(resolve => setTimeout(resolve, 300));

        onImport(leads, selectedFolderId || undefined);
        handleClose();
      } catch (err) {
        console.error("Error importing leads:", err);
        setError("Fehler beim Importieren der Leads");
        setStep("preview");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Leads importieren (CSV/Excel)</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === "upload" && (
          <div className="space-y-4 py-4">
            {marketplaceFields && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">Preis pro Lead (€)</Label>
                    <Input
                      id="price"
                      type="number"
                      min="1"
                      value={marketplaceFields.price}
                      onChange={(e) => marketplaceFields.onPriceChange(e.target.value)}
                      placeholder="100"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="folder">Ordner</Label>
                    <div className="flex gap-2">
                      <Select
                        value={marketplaceFields.folderName}
                        onValueChange={marketplaceFields.onFolderChange}
                      >
                        <SelectTrigger id="folder">
                          <SelectValue placeholder="Ordner wählen oder neu erstellen" />
                        </SelectTrigger>
                        <SelectContent>
                          {marketplaceFields.folders.map(folder => (
                            <SelectItem key={folder} value={folder}>
                              {folder}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Neuer Ordner"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (newFolderName.trim()) {
                              marketplaceFields.onFolderChange(newFolderName.trim());
                              setNewFolderName("");
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (newFolderName.trim()) {
                            marketplaceFields.onFolderChange(newFolderName.trim());
                            setNewFolderName("");
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={marketplaceFields.description}
                    onChange={(e) => marketplaceFields.onDescriptionChange(e.target.value)}
                    placeholder="Beschreiben Sie die Leads und warum sie wertvoll sind"
                  />
                </div>
              </div>
            )}

            {!marketplaceFields && userFolders && userFolders.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="folder-select">Ordner (optional)</Label>
                <Select
                  value={selectedFolderId || "none"}
                  onValueChange={(value) => setSelectedFolderId(value === "none" ? null : value)}
                >
                  <SelectTrigger id="folder-select">
                    <SelectValue placeholder="Kein Ordner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Ordner</SelectItem>
                    {userFolders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12">
              <FileUp className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600 mb-4">
                Wählen Sie eine CSV- oder Excel-Datei aus
              </p>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="max-w-sm"
              />
            </div>
          </div>
        )}

        {step === "mapping" && (
          <div className="space-y-4 py-4">
            <p className="text-sm">Ordnen Sie die Spalten aus Ihrer Datei den Lead-Feldern zu:</p>

            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Spaltenname</TableHead>
                    <TableHead>Zugeordnetes Feld</TableHead>
                    <TableHead>Beispielwert</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {headers.map((header, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{translateHeaderToGerman(header)}</TableCell>
                      <TableCell>
                        <Select
                          value={mappings[index.toString()] || "not_mapped"}
                          onValueChange={(value) => handleMappingChange(index.toString(), value)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Feld auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_mapped">Nicht zuordnen</SelectItem>
                            {DEFAULT_MAPPINGS.map((mapping) => (
                              <SelectItem key={mapping.field} value={mapping.field}>
                                {mapping.header}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="truncate max-w-[200px]">
                        {previewData[0]?.[index] || ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Button onClick={handlePreview} className="ml-auto">
              Vorschau
            </Button>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4 py-4">
            <p className="text-sm">Vorschau der zu importierenden Daten:</p>

            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {Object.entries(mappings)
                      .filter(([_, field]) => field !== "not_mapped")
                      .map(([columnIndex, field]) => (
                        <TableHead key={columnIndex}>
                          {DEFAULT_MAPPINGS.find(m => m.field === field)?.header || field}
                        </TableHead>
                      ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {Object.entries(mappings)
                        .filter(([_, field]) => field !== "not_mapped")
                        .map(([columnIndex, _]) => (
                          <TableCell key={columnIndex} className="truncate max-w-[200px]">
                            {row[parseInt(columnIndex)] || ""}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <p className="text-sm text-muted-foreground mt-4">
              {previewData.length} von {totalRows} Zeilen werden angezeigt.
              Beim Import werden alle {totalRows} Zeilen verarbeitet.
            </p>
          </div>
        )}

        {step === "importing" && (
          <div className="space-y-6 py-8">
            <div className="flex flex-col items-center justify-center text-center gap-4">
              <LoaderCircle className="h-12 w-12 text-primary animate-spin" />
              <h3 className="text-lg font-medium">{importingStatus}</h3>
              <div className="w-full max-w-md">
                <Progress value={importProgress} className="h-2" />
                <p className="text-sm text-muted-foreground mt-2">{importProgress}% abgeschlossen</p>
              </div>
              <p className="text-sm text-muted-foreground max-w-md">
                Bei großen Dateien kann dieser Vorgang mehrere Minuten dauern. Bitte schließen Sie dieses Fenster nicht.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step !== "upload" && step !== "importing" && (
            <Button type="button" variant="outline" onClick={() => setStep(step === "preview" ? "mapping" : "upload")}>
              Zurück
            </Button>
          )}

          {step === "upload" && (
            <Button type="button" variant="outline" onClick={handleClose}>
              Abbrechen
            </Button>
          )}

          {step === "preview" && (
            <Button
              onClick={handleImport}
              disabled={
                marketplaceFields && (!marketplaceFields.price || !marketplaceFields.folderName)
              }
            >
              <Upload className="mr-2 h-4 w-4" />
              Importieren ({totalRows} Leads)
            </Button>
          )}

          {step === "importing" && (
            <Button type="button" disabled>
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              Bitte warten...
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
